import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase, resolveUserRole, homePathForRole } from '@/lib/supabase';
import { rememberSession } from '@/components/AuthProvider';
import { normalizePhone } from '@/lib/format';
import AuthLayout from './AuthLayout';
import styles from './auth.module.css';

export default function Login() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    const trimmed = identifier.trim();
    if (!trimmed || !password) {
      setError('Veuillez saisir votre email ou numéro de téléphone et votre mot de passe.');
      return;
    }

    const isEmail = trimmed.includes('@');
    const phone = isEmail ? null : normalizePhone(trimmed);
    if (!isEmail && !phone) {
      setError('Numéro de téléphone invalide.');
      return;
    }

    setLoading(true);
    try {
      const { error: authError } = await supabase.auth.signInWithPassword(
        isEmail ? { email: trimmed.toLowerCase(), password } : { phone: phone!, password }
      );

      if (authError) {
        setError(
          authError.message === 'Invalid login credentials'
            ? 'Identifiants incorrects.'
            : authError.message
        );
        return;
      }

      rememberSession(remember);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        const role = await resolveUserRole(user.id);
        if (role === null) {
          await supabase.auth.signOut();
          setError('Ce compte a été suspendu. Contactez le support.');
          return;
        }
        navigate(homePathForRole(role), { replace: true });
      }
    } catch {
      setError('Une erreur est survenue. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      hero
      title="Connectez-vous"
      footer={
        <>
          Pas de compte ? <Link to="/signup">Créer un compte</Link>
        </>
      }
    >
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {error ? (
          <p className="alert alert--error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="field">
          <label className="label" htmlFor="identifier">
            Email ou téléphone
          </label>
          <input
            id="identifier"
            className="input"
            type="text"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            placeholder="your@email.com ou 0612345678"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            disabled={loading}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="password">
            Mot de passe
          </label>
          <input
            id="password"
            className="input"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={loading}
          />
        </div>

        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={remember}
            onChange={(event) => setRemember(event.target.checked)}
            disabled={loading}
          />
          Rester connecté
        </label>

        <Link className={styles.inlineLink} to="/forgot-password">
          Mot de passe oublié ?
        </Link>

        <button type="submit" className="btn btn--primary btn--block" disabled={loading}>
          {loading ? 'Connexion en cours...' : 'Se connecter'}
        </button>
      </form>
    </AuthLayout>
  );
}
